import { ValidationMessage } from "@/flokit/core/validation-message/validation-message";
import { useHtmlId } from "@/flokit/utils/html/use-html-id";
import { cx } from "class-variance-authority";
import React, { forwardRef } from "react";
import TextArea from "react-textarea-autosize";
import { useIsFormDisabled } from "../contexts/disabled-form-context";
import { InputLabel } from "../input-label/input-label";
import {
  getCorrectHtmlAttributesForInput,
  normalizeLabelProp,
} from "../input-label/label-prop";
import { invalidInputOutlineClasses } from "../shared-styles";
import {
  InputValidationProps,
  LabelledInputProps,
  SelectionRange,
} from "../types";
import { CharacterLimit } from "@/flokit/compound/inputs/text-area-input/character-limit";

export type AutoResizeTextAreaInputProps = LabelledInputProps &
  InputValidationProps<HTMLTextAreaElement> & {
    value: string;
    onChange: (
      newValue: string,
      event?: React.ChangeEvent<HTMLTextAreaElement>,
    ) => void;

    isDisabled?: boolean;

    placeholder?: string;

    rows?: number;

    onSelect?: (newSelection: SelectionRange) => void;
    characterLimit?: number;
  };

export const AutoResizeTextAreaInput = forwardRef<
  HTMLTextAreaElement,
  AutoResizeTextAreaInputProps
>((props, ref) => {
  const isFormDisabled = useIsFormDisabled();
  const generatedId = useHtmlId();
  const labelMethod = normalizeLabelProp(props.label);
  const isDisabled = props.isDisabled ?? isFormDisabled;

  const characterLimitId = useHtmlId();

  return (
    <div
      className={cx(
        "flex flex-col",
        labelMethod.type !== "hidden" && "gap-y-2",
        "w-full",
      )}
    >
      {labelMethod.type === "default" || labelMethod.type === "hidden" ? (
        <div className="flex flex-row items-center justify-between">
          <div>
            <InputLabel
              labelMethod={labelMethod}
              interactivity="interactive"
              isRequired={props.isRequired}
              isDisabled={isDisabled}
              tooltip={props.tooltip}
              idOfElementToBeLabeled={generatedId}
            />
          </div>
          {props.characterLimit ? (
            <CharacterLimit
              currentLength={props.value?.length ?? 0}
              characterLimit={props.characterLimit}
              shouldShowValidation={props.shouldShowValidation ?? false}
              id={characterLimitId}
            />
          ) : null}
        </div>
      ) : null}
      <TextArea
        ref={ref}
        data-testid="auto-resize-text-area"
        value={props.value}
        onChange={(e) => {
          props.onChange(e.target.value, e);
        }}
        placeholder={props.placeholder}
        {...getCorrectHtmlAttributesForInput({
          labelProp: props.label,
          htmlId: generatedId,
          elementIsNonInteractive: false,
          fieldIsRequired: props.isRequired,
        })}
        disabled={isDisabled}
        minRows={props.rows}
        onFocus={(e) => {
          props.onFocus?.(e);
        }}
        onBlur={(e) => {
          props.onBlur?.(e);
        }}
        onSelect={(e) =>
          props?.onSelect?.({
            start: (e.target as HTMLInputElement).selectionStart,
            end: (e.target as HTMLInputElement).selectionEnd,
          })
        }
        className={cx(
          "resize-none appearance-none",
          "px-4 py-4",
          "rounded border border-dublin-gray",
          "text-base",
          "focus:border-flo-magenta focus:outline-none focus:ring-1 focus:ring-flo-magenta",
          "disabled:text-light-elephant",
          props.shouldShowValidation && invalidInputOutlineClasses,
        )}
        aria-describedby={props.characterLimit ? characterLimitId : undefined}
      />
      <ValidationMessage
        shouldShowValidation={props.shouldShowValidation}
        validationMessage={props.validationMessage}
      />
    </div>
  );
});
